/**
 * Charting Manager - Real-time Temperature & RoR Visualization
 * Using Chart.js for high-performance charting
 */

import Chart from 'chart.js/auto';
import 'chartjs-adapter-date-fns';

class ChartingManager {
    constructor() {
        this.tempChart = null;
        this.rorChart = null;
        this.sessionStartTime = null;
        this.dataPoints = {
            bt: [],
            et: [],
            ror: [],
            milestones: []
        };
        this.maxDataPoints = 3600; // 1 hour at 1Hz
        this.rorWindow = 60; // 60 seconds for RoR calculation
    }

    /**
     * Initialize temperature chart
     */
    initTempChart(canvasId) {
        const ctx = document.getElementById(canvasId);
        if (!ctx) {
            console.error('Canvas not found:', canvasId);
            return;
        }

        this.tempChart = new Chart(ctx, {
            type: 'line',
            data: {
                datasets: [
                    {
                        label: 'Bean Temp (BT)',
                        data: [],
                        borderColor: '#ff6b35',
                        backgroundColor: 'rgba(255, 107, 53, 0.1)',
                        borderWidth: 3,
                        tension: 0.4,
                        pointRadius: 0,
                        pointHoverRadius: 6,
                        fill: false
                    },
                    {
                        label: 'Environment Temp (ET)',
                        data: [],
                        borderColor: '#3b82f6',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        borderWidth: 3,
                        tension: 0.4,
                        pointRadius: 0,
                        pointHoverRadius: 6,
                        fill: false
                    },
                    {
                        label: 'Ghost BT',
                        data: [],
                        borderColor: 'rgba(255, 107, 53, 0.3)',
                        borderWidth: 2,
                        borderDash: [5, 5],
                        tension: 0.4,
                        pointRadius: 0,
                        fill: false,
                        hidden: true
                    },
                    {
                        label: 'Ghost ET',
                        data: [],
                        borderColor: 'rgba(59, 130, 246, 0.3)',
                        borderWidth: 2,
                        borderDash: [5, 5],
                        tension: 0.4,
                        pointRadius: 0,
                        fill: false,
                        hidden: true
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: 'index',
                    intersect: false
                },
                plugins: {
                    title: {
                        display: false
                    },
                    legend: {
                        display: true,
                        position: 'top',
                        labels: {
                            color: '#ffffff',
                            font: {
                                size: 14,
                                weight: '600'
                            },
                            usePointStyle: true,
                            padding: 20
                        }
                    },
                    tooltip: {
                        backgroundColor: 'rgba(26, 26, 26, 0.95)',
                        titleColor: '#ffffff',
                        bodyColor: '#ffffff',
                        borderColor: '#ff6b35',
                        borderWidth: 1,
                        padding: 12,
                        displayColors: true,
                        callbacks: {
                            label: function (context) {
                                let label = context.dataset.label || '';
                                if (label) {
                                    label += ': ';
                                }
                                label += context.parsed.y.toFixed(1) + '°C';
                                return label;
                            },
                            title: function (tooltipItems) {
                                const seconds = tooltipItems[0].parsed.x;
                                const minutes = Math.floor(seconds / 60);
                                const secs = Math.floor(seconds % 60);
                                return `Time: ${minutes}:${secs.toString().padStart(2, '0')}`;
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        type: 'linear',
                        title: {
                            display: true,
                            text: 'Time (seconds)',
                            color: '#b0b0b0',
                            font: {
                                size: 14,
                                weight: '600'
                            }
                        },
                        ticks: {
                            color: '#b0b0b0',
                            callback: function (value) {
                                const minutes = Math.floor(value / 60);
                                const seconds = Math.floor(value % 60);
                                return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                            }
                        },
                        grid: {
                            color: 'rgba(255, 255, 255, 0.05)'
                        }
                    },
                    y: {
                        title: {
                            display: true,
                            text: 'Temperature (°C)',
                            color: '#b0b0b0',
                            font: {
                                size: 14,
                                weight: '600'
                            }
                        },
                        ticks: {
                            color: '#b0b0b0'
                        },
                        grid: {
                            color: 'rgba(255, 255, 255, 0.05)'
                        },
                        beginAtZero: false
                    }
                },
                animation: {
                    duration: 0 // Disable animations for real-time performance
                }
            }
        });

        console.log('Temperature chart initialized');
    }

    /**
     * Initialize Rate of Rise chart
     */
    initRoRChart(canvasId) {
        const ctx = document.getElementById(canvasId);
        if (!ctx) {
            console.error('Canvas not found:', canvasId);
            return;
        }

        this.rorChart = new Chart(ctx, {
            type: 'line',
            data: {
                datasets: [
                    {
                        label: 'Rate of Rise (RoR)',
                        data: [],
                        borderColor: '#a855f7',
                        backgroundColor: 'rgba(168, 85, 247, 0.1)',
                        borderWidth: 3,
                        tension: 0.4,
                        pointRadius: 0,
                        pointHoverRadius: 6,
                        fill: true
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: 'index',
                    intersect: false
                },
                plugins: {
                    legend: {
                        display: true,
                        position: 'top',
                        labels: {
                            color: '#ffffff',
                            font: {
                                size: 14,
                                weight: '600'
                            },
                            usePointStyle: true,
                            padding: 20
                        }
                    },
                    tooltip: {
                        backgroundColor: 'rgba(26, 26, 26, 0.95)',
                        titleColor: '#ffffff',
                        bodyColor: '#ffffff',
                        borderColor: '#a855f7',
                        borderWidth: 1,
                        padding: 12,
                        callbacks: {
                            label: function (context) {
                                return 'RoR: ' + context.parsed.y.toFixed(2) + '°C/min';
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        type: 'linear',
                        title: {
                            display: true,
                            text: 'Time (seconds)',
                            color: '#b0b0b0',
                            font: {
                                size: 14,
                                weight: '600'
                            }
                        },
                        ticks: {
                            color: '#b0b0b0',
                            callback: function (value) {
                                const minutes = Math.floor(value / 60);
                                const seconds = Math.floor(value % 60);
                                return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                            }
                        },
                        grid: {
                            color: 'rgba(255, 255, 255, 0.05)'
                        }
                    },
                    y: {
                        title: {
                            display: true,
                            text: 'RoR (°C/min)',
                            color: '#b0b0b0',
                            font: {
                                size: 14,
                                weight: '600'
                            }
                        },
                        ticks: {
                            color: '#b0b0b0'
                        },
                        grid: {
                            color: 'rgba(255, 255, 255, 0.05)'
                        }
                    }
                },
                animation: {
                    duration: 0
                }
            }
        });

        console.log('RoR chart initialized');
    }

    /**
     * Add data point to charts
     */
    addDataPoint(bt, et, timestamp) {
        if (!timestamp) {
            timestamp = Date.now();
        }

        // Calculate elapsed time in seconds
        if (!this.sessionStartTime) {
            this.sessionStartTime = timestamp;
        }
        const elapsedSeconds = (timestamp - this.sessionStartTime) / 1000;

        // Store data points
        this.dataPoints.bt.push({ x: elapsedSeconds, y: bt });
        this.dataPoints.et.push({ x: elapsedSeconds, y: et });

        // Limit data points
        if (this.dataPoints.bt.length > this.maxDataPoints) {
            this.dataPoints.bt.shift();
            this.dataPoints.et.shift();
        }

        // Calculate RoR
        const ror = this.calculateRoR();
        if (ror !== null) {
            this.dataPoints.ror.push({ x: elapsedSeconds, y: ror });
            if (this.dataPoints.ror.length > this.maxDataPoints) {
                this.dataPoints.ror.shift();
            }
        }

        // Update charts
        this.updateCharts();

        return { bt, et, ror, elapsedSeconds };
    }

    /**
     * Calculate Rate of Rise (RoR)
     * RoR = (Current BT - BT n seconds ago) / n * 60
     */
    calculateRoR() {
        if (this.dataPoints.bt.length < 2) {
            return null;
        }

        // Find data point from rorWindow seconds ago
        const currentPoint = this.dataPoints.bt[this.dataPoints.bt.length - 1];
        const currentTime = currentPoint.x;
        const targetTime = currentTime - this.rorWindow;

        // Find closest past point
        let pastPoint = null;
        for (let i = this.dataPoints.bt.length - 2; i >= 0; i--) {
            if (this.dataPoints.bt[i].x <= targetTime) {
                pastPoint = this.dataPoints.bt[i];
                break;
            }
        }

        if (!pastPoint) {
            return null;
        }

        // Calculate RoR
        const timeDiff = currentPoint.x - pastPoint.x;
        const tempDiff = currentPoint.y - pastPoint.y;
        const ror = (tempDiff / timeDiff) * 60; // Convert to per minute

        return ror;
    }

    /**
     * Update charts with current data
     */
    updateCharts() {
        if (this.tempChart) {
            this.tempChart.data.datasets[0].data = this.dataPoints.bt;
            this.tempChart.data.datasets[1].data = this.dataPoints.et;
            this.tempChart.update('none'); // Update without animation
        }

        if (this.rorChart) {
            this.rorChart.data.datasets[0].data = this.dataPoints.ror;
            this.rorChart.update('none');
        }
    }

    /**
     * Add milestone marker to chart
     */
    addMilestone(label, color = '#fbbf24') {
        if (!this.sessionStartTime) return;

        const elapsedSeconds = (Date.now() - this.sessionStartTime) / 1000;

        this.dataPoints.milestones.push({
            time: elapsedSeconds,
            label: label,
            color: color
        });

        // Add annotation to temperature chart
        if (this.tempChart) {
            const currentBT = this.dataPoints.bt[this.dataPoints.bt.length - 1]?.y || 0;

            // Add vertical line and label (simplified - would need chart.js annotation plugin for full feature)
            console.log(`Milestone added: ${label} at ${elapsedSeconds.toFixed(0)}s, BT: ${currentBT.toFixed(1)}°C`);
        }
    }

    /**
     * Reset charts and data
     */
    reset() {
        this.sessionStartTime = null;
        this.dataPoints = {
            bt: [],
            et: [],
            ror: [],
            milestones: []
        };
        this.updateCharts();
        console.log('Charts reset');
    }

    /**
     * Export chart as image
     */
    exportChart(chartType = 'temp') {
        const chart = chartType === 'temp' ? this.tempChart : this.rorChart;
        if (!chart) return null;

        const url = chart.toBase64Image();
        return url;
    }

    /**
     * Get current data points
     */
    getData() {
        return {
            bt: [...this.dataPoints.bt],
            et: [...this.dataPoints.et],
            ror: [...this.dataPoints.ror],
            milestones: [...this.dataPoints.milestones]
        };
    }

    /**
     * Load existing data into charts
     */
    loadData(data) {
        this.dataPoints.bt = data.bt || [];
        this.dataPoints.et = data.et || [];
        this.dataPoints.ror = data.ror || [];
        this.dataPoints.milestones = data.milestones || [];

        if (this.dataPoints.bt.length > 0) {
            this.sessionStartTime = Date.now() - (this.dataPoints.bt[this.dataPoints.bt.length - 1].x * 1000);
        }

        this.updateCharts();
    }

    /**
     * Load ghost data to background datasets
     */
    loadGhostData(data) {
        if (!this.tempChart) return;
        
        // Ensure datasets 2 and 3 are visible
        this.tempChart.data.datasets[2].data = data.bt || [];
        this.tempChart.data.datasets[3].data = data.et || [];
        this.tempChart.data.datasets[2].hidden = false;
        this.tempChart.data.datasets[3].hidden = false;
        
        this.tempChart.update('none');
        console.log('Ghost data loaded to chart');
    }

    /**
     * Destroy charts
     */
    destroy() {
        if (this.tempChart) {
            this.tempChart.destroy();
            this.tempChart = null;
        }
        if (this.rorChart) {
            this.rorChart.destroy();
            this.rorChart = null;
        }
    }
}

export default ChartingManager;
